import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { UserPlus } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ConfirmButton, TextField } from '@/components/bookings/dialogKit'
import { createCustomer, listCustomers, updateCustomerProfile } from '@/lib/api'
import { apiErrorReason, parseApiError, readApiError } from '@/lib/apiError'
import type { CustomerMatch } from '@/hooks/useCustomerSearch'

// ─── "New client" ──────────────────────────────────────────────────────────────
//
// Opened from the customer step of the booking dialog when the person in front
// of the operator is not on file yet. It is the FIRST typed-value form on the
// calendar, so the boundary matters more here than anywhere else in this folder:
//
//   IT WRITES IDENTITY, AND NOTHING ELSE. A name, a mobile, an email, a date of
//   birth. Never a booking, never a price, never a package, never a note, never
//   a health detail. Anything about what this person is buying or what is wrong
//   with their knee is still chat's job — and chat can create a customer too
//   (create_customer exists in v1-chat and over MCP), so this dialog is a faster
//   route to an existing capability rather than a new one.
//
// WHY THESE FIELDS CANNOT BE CHOICES: a new person's name and contact details
// are not on any server-supplied list by definition. That is the one honest
// exception to the choose-don't-type rule the calendar dialogs otherwise hold,
// and it is why the field set is closed rather than open.

export interface NewClientDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Handed the created (or matched existing) customer, ready to book. */
  onCreated: (customer: CustomerMatch) => void
}

/**
 * First + last become the single `customers.name` column. Splitting them in the
 * database would have to be threaded through find_customer, search_customers,
 * @-mentions, invoices and every report, all of which read one name today.
 */
function composeName(first: string, last: string): string {
  return [first.trim(), last.trim()].filter(Boolean).join(' ')
}

export function NewClientDialog({ open, onOpenChange, onCreated }: NewClientDialogProps) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [dob, setDob] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setFirstName('')
    setLastName('')
    setPhone('')
    setEmail('')
    setDob('')
    setSaving(false)
    setError(null)
  }, [open])

  const name = composeName(firstName, lastName)
  // Phone is the only hard requirement, because `customers.phone` is not-null
  // and unique — it is how an inbound text resolves to a person, so a record
  // without one cannot be reached on the channel this business runs on.
  const canSave = name.length > 0 && phone.trim().length > 0 && !saving

  /**
   * A duplicate is not really an error: the operator is trying to book somebody
   * who is already on file. Find them and hand them back rather than making the
   * operator close this, remember what the search box was for, and start again.
   */
  async function useExisting(query: string, message: string): Promise<boolean> {
    try {
      const page = await listCustomers({ search: query, limit: 1 })
      const match = page.items[0]
      if (!match) return false
      toast.info(message, { description: `Using the existing record for ${match.name ?? 'them'}.` })
      onCreated({ id: match.id, name: match.name ?? null, phone: match.phone ?? null })
      onOpenChange(false)
      return true
    } catch {
      return false
    }
  }

  async function save() {
    if (!canSave) return
    setSaving(true)
    setError(null)
    try {
      const created = await createCustomer({
        phone: phone.trim(),
        name,
        email: email.trim() || null,
      })

      // The profile is a separate row, and the customer already exists by the
      // time this runs — so a failure here must not read as "the client was not
      // created". Say what is missing and carry on to the booking.
      if (dob) {
        try {
          await updateCustomerProfile(created.id, { dob })
        } catch (e) {
          toast.warning('Client added, but the date of birth did not save', {
            description: readApiError(e),
          })
        }
      }

      toast.success(`${name} added`)
      onCreated({ id: created.id, name: created.name ?? name, phone: created.phone ?? phone.trim() })
      onOpenChange(false)
    } catch (e) {
      // v1-customers puts the specific collision in the envelope's `reason` — its
      // context object is spread AFTER the generic code, so it wins. That is what
      // makes these two distinguishable without matching on message text.
      const reason = apiErrorReason(e)
      if (reason === 'phone_already_exists') {
        const handled = await useExisting(phone.trim(), 'That mobile is already on file')
        if (handled) return
        setError('That mobile number already belongs to another client.')
      } else if (reason === 'email_already_exists') {
        const handled = await useExisting(email.trim(), 'That email is already on file')
        if (handled) return
        setError('That email address already belongs to another client.')
      } else if (reason === 'validation_error') {
        const fields = (parseApiError(e)?.fields ?? []) as string[]
        setError(
          fields.includes('phone')
            ? "That mobile number isn't valid for this region — include the area or country code."
            : readApiError(e),
        )
      } else if (reason === 'setup_required') {
        setError('Clients cannot be added until business setup is finished.')
      } else {
        setError(readApiError(e))
      }
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New client</DialogTitle>
          <DialogDescription>
            Just who they are. Health details, goals and packages are asked for in chat.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <TextField label="First name" value={firstName} onChange={setFirstName} autoFocus />
            <TextField label="Last name" value={lastName} onChange={setLastName} />
          </div>

          <TextField
            label="Mobile"
            value={phone}
            onChange={setPhone}
            type="tel"
            inputMode="tel"
            placeholder="04xx xxx xxx"
            hint="Required — it is how texts and reminders reach them."
          />

          <TextField
            label="Email"
            value={email}
            onChange={setEmail}
            type="email"
            inputMode="email"
            hint="Optional. Needed for receipts and to invite them to the app."
          />

          <TextField
            label="Date of birth"
            value={dob}
            onChange={setDob}
            type="date"
            hint="Optional."
          />

          {error ? <p className="text-[12.5px] text-destructive">{error}</p> : null}

          <div className="flex flex-col gap-2 border-t border-border pt-3">
            <ConfirmButton
              label={name ? `Add ${name}` : 'Add client'}
              icon={<UserPlus className="size-4" aria-hidden />}
              busy={saving}
              disabled={!canSave}
              onClick={() => void save()}
            />
            <p className="text-center text-[11px] text-muted-foreground">
              They are added to the client list and selected for this booking.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
